/**
 * appointmentActions.ts
 *
 * "Action Required" is the answer to "this visit happened — is anything still
 * outstanding on it?": the patient was never checked out, required products were
 * never disbursed, or a purchase is still owed.
 *
 * It's derived from two endpoints that have to be read together — the
 * appointment row (`checkedOutAt`, `noShow`, cancelled) and the patient's
 * product types (`/patients/{id}/product-types`) — so both the Appointments tab
 * and the sidebar's Recent Visits cards share this module rather than each
 * rebuilding the rule. Two copies would eventually disagree about what counts as
 * outstanding, and the two views sit on the same screen.
 */
import type {
  ProductGroup,
  ProductItem,
  ProductTypesResponse,
} from '@/components/patients/PatientProductsDisplay';

export interface ApptProductStats {
  totalRequired: number;
  totalAllocated: number;
  totalDisbursed: number;
  purchaseRequired: number;
  allAllocated: boolean;
  allDisbursed: boolean;
}

/** The product endpoint returns groups keyed by id, but sends `[]` when empty. */
function toArr<T>(v: T[] | Record<string, T> | undefined | null): T[] {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  return Object.values(v);
}

export function buildApptProductStats(
  data: ProductTypesResponse | undefined,
): Map<number, ApptProductStats> {
  const map = new Map<number, ApptProductStats>();
  if (!data?.data) return map;

  function getOrCreate(apptId: number): ApptProductStats {
    let s = map.get(apptId);
    if (!s) {
      s = { totalRequired: 0, totalAllocated: 0, totalDisbursed: 0, purchaseRequired: 0, allAllocated: false, allDisbursed: false };
      map.set(apptId, s);
    }
    return s;
  }

  const sections: Record<string, ProductGroup>[] = [
    data.data['product-types'] ?? {},
    data.data['product-others'] ?? {},
  ];

  for (const section of sections) {
    for (const group of Object.values(section)) {
      for (const item of toArr<ProductItem>(group.appointments)) {
        if (!item.scheduledAppointmentsID) continue;
        const s = getOrCreate(item.scheduledAppointmentsID);
        s.totalRequired += item.quantity;
        s.totalAllocated += item.quantity;
      }

      for (const item of toArr<ProductItem>(group.purchase)) {
        if (!item.scheduledAppointmentsID) continue;
        const s = getOrCreate(item.scheduledAppointmentsID);
        s.totalRequired += item.quantity;
        s.purchaseRequired += item.quantity;
      }

      for (const item of toArr<ProductItem>(group.unavailable)) {
        if (!item.scheduledAppointmentsID) continue;
        if (!item.usedDate || item.usedDate === '0000-00-00 00:00:00') continue;
        const s = getOrCreate(item.scheduledAppointmentsID);
        s.totalDisbursed += item.quantity;
      }
    }
  }

  for (const s of map.values()) {
    s.allAllocated = s.totalRequired > 0 && s.totalAllocated >= s.totalRequired;
    s.allDisbursed = s.totalRequired > 0 && s.totalDisbursed >= s.totalRequired;
  }

  return map;
}

/** The appointment fields the rule reads, in whichever shape the caller has. */
export interface AppointmentActionSubject {
  checkedOutAt?: string | null;
  noShow?: boolean;
  cancelled?: boolean;
  deleted?: boolean;
}

/**
 * Outstanding items on a PAST appointment, as display labels. Empty means
 * nothing is required — callers use `length > 0` as the "action required" test.
 *
 * Callers must only pass past appointments: a future visit is legitimately not
 * checked out yet, so this would flag every one of them.
 *
 * A cancelled, no-show, or deleted appointment can't be checked out or
 * disbursed against, so it never requires action.
 */
export function getAppointmentActionReasons(
  appointment: AppointmentActionSubject,
  stats: ApptProductStats | undefined,
): string[] {
  if (appointment.deleted || appointment.cancelled || appointment.noShow) return [];

  const reasons: string[] = [];
  if (!appointment.checkedOutAt) reasons.push('Not Checked Out');
  if (stats && stats.totalRequired > 0 && !stats.allDisbursed) reasons.push('Products Not Disbursed');
  if (stats && stats.purchaseRequired > 0) reasons.push('Purchase Required');
  return reasons;
}
